import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import '../Subscription/Subscription.scss';
import white_locshark from '../../assets/images/white_locshark.png';
import Basic_dashboard from '../../assets/images/basic-dashboard.png';
import MapPinpoint from '../../assets/images/MapPinpoint.png';
import Zones from '../../assets/images/Zones.png';
import Reports from '../../assets/images/reports.png';
import useApi from '../../hook/useAPI';
import useUserDetailsRefresh from '../../hook/useUserDetailsRefresh';
import { SUBSCRIPTION, USER_MANAGEMENT } from '../../utils/const';
import { useLocation } from 'react-router-dom';
import PaymentResultModal from './PaymentResultModal';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import {
  buildActivePlanResult,
  buildBillingReturnResult,
  buildCheckoutSuccessResult,
  buildPaymentCancelledResult,
} from '../../utils/paymentResultSummary';
import { hasManageableSubscriptionStatus } from '../../constants/subscriptionStatus';
import { openStripeCustomerPortal } from '../../utils/stripePortal';
import {
  billingFingerprintChanged,
  buildBillingFingerprint,
  clearBillingSnapshot,
  loadBillingSnapshot,
} from '../../utils/billingSnapshot';
import {
  formatCurrencyAmount,
  getDisplayPlanName,
  isAddonPlanName,
  isBasicPlanName,
  isFullPlanName,
  PLAN_KEYS,
} from '../../utils/planCatalogDisplay';
import { getPaymentErrorMessage, redirectToStripeCheckout } from '../../utils/subscriptionCheckout';

const ADDON_PLAN_ORDER = ['Trips', 'Zones', 'Report', 'Reports'];

const imageMap = {
  'All-in-One Bundle': white_locshark,
  Basic: Basic_dashboard,
  Trips: MapPinpoint,
  Zones,
  Reports,
  Report: Reports,
};

function Subscription({ returnPath = '/main/account', embedded = false }) {
  const { activePlan, refreshUserDetails } = useUserDetailsRefresh();
  const syncedPlanKeyRef = useRef('');
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showPaymentResult, setShowPaymentResult] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const { getData, postData } = useApi();
  const stripe = useStripe();
  const location = useLocation();

  const stripeCatalogPlans = useMemo(
    () => plans.filter((plan) => plan.isStripePlan && !plan.isTrial),
    [plans],
  );

  const subscriptionPlans = useMemo(() => {
    const fullPlan = stripeCatalogPlans.find(
      (plan) => plan.planKey === PLAN_KEYS.ALL_IN_ONE || isFullPlanName(plan),
    );
    const basicPlanEntry = stripeCatalogPlans.find(
      (plan) => plan.planKey === PLAN_KEYS.BASIC || isBasicPlanName(plan),
    );
    return [fullPlan, basicPlanEntry].filter(Boolean);
  }, [stripeCatalogPlans]);

  const sortedAddonPlans = useMemo(() => (
    stripeCatalogPlans
      .filter((plan) => isAddonPlanName(plan))
      .sort((left, right) => {
        const leftIndex = ADDON_PLAN_ORDER.indexOf(left.planName);
        const rightIndex = ADDON_PLAN_ORDER.indexOf(right.planName);
        return (leftIndex === -1 ? 99 : leftIndex) - (rightIndex === -1 ? 99 : rightIndex);
      })
  ), [stripeCatalogPlans]);

  const basicPlan = useMemo(
    () => subscriptionPlans.find((plan) => isBasicPlanName(plan)),
    [subscriptionPlans],
  );

  const syncLocalPlanState = (plan) => {
    if (!plan) {
      return null;
    }
    if (plan.subscription?.billingPeriod) {
      setBillingPeriod(plan.subscription.billingPeriod);
    }
    if (
      plan.subscription?.pendingAddonChangeAt
      && Array.isArray(plan.subscription?.pendingAddonPlanIds)
    ) {
      setSelectedAddonIds(plan.subscription.pendingAddonPlanIds);
    } else if (Array.isArray(plan.addons)) {
      setSelectedAddonIds(plan.addons);
    }
    return plan;
  };

  const fetchUserDetails = async (showLoader = true) => {
    const users = await refreshUserDetails(showLoader);
    return syncLocalPlanState(users?.activePlan);
  };

  const ensurePlansLoaded = async () => {
    if (plans.length > 0) {
      return plans;
    }
    try {
      const response = await getData(`${SUBSCRIPTION.GET_PRICE}`, null, false);
      const loadedPlans = Array.isArray(response?.data) ? response.data : [];
      setPlans(loadedPlans);
      return loadedPlans;
    } catch (error) {
      console.error('Error fetching plans:', error);
      return [];
    }
  };

  const showSimpleToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
  };

  useEffect(() => {
    if (!activePlan) {
      return;
    }
    const syncKey = JSON.stringify({
      billingId: activePlan?.billing?._id || activePlan?.billing || null,
      addons: activePlan?.addons || [],
      billingPeriod: activePlan?.subscription?.billingPeriod || null,
      pendingAddons: activePlan?.subscription?.pendingAddonPlanIds || [],
    });
    if (syncKey === syncedPlanKeyRef.current) {
      return;
    }
    syncedPlanKeyRef.current = syncKey;
    syncLocalPlanState(activePlan);
  }, [activePlan]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await getData(`${SUBSCRIPTION.GET_PRICE}`);
        setPlans(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching plans:', error);
      }
    };

    fetchPlans();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const billingReturn = params.get('billing');
    const paymentStatus = params.get('paymentStatus');
    const sessionId = params.get('session_id');

    const handlePaymentReturn = async () => {
      const planList = await ensurePlansLoaded();

      if (billingReturn === 'return') {
        const beforeSnapshot = loadBillingSnapshot();
        const updatedPlan = await fetchUserDetails(false);
        const afterSnapshot = buildBillingFingerprint(updatedPlan);
        clearBillingSnapshot();
        window.history.replaceState(null, '', window.location.pathname);

        if (billingFingerprintChanged(beforeSnapshot, afterSnapshot)) {
          setPaymentResult(buildBillingReturnResult(updatedPlan, planList));
          setShowPaymentResult(true);
        }
        return;
      }

      if (paymentStatus === 'success') {
        if (sessionId) {
          try {
            await postData(
              `${USER_MANAGEMENT.SYNC_CHECKOUT_SESSION}/${sessionId}`,
              {},
              null,
            );
            const summaryResponse = await getData(
              `${USER_MANAGEMENT.CHECKOUT_SESSION_SUMMARY}/${sessionId}`,
              null,
              false,
            );
            if (summaryResponse?.data) {
              setPaymentResult(buildCheckoutSuccessResult(summaryResponse.data));
              setShowPaymentResult(true);
              await fetchUserDetails(false);
              window.history.replaceState(null, '', window.location.pathname);
              return;
            }
          } catch (error) {
            console.error('Error fetching checkout summary:', error);
          }
        }

        const updatedPlan = await fetchUserDetails(false);
        setPaymentResult(buildActivePlanResult(updatedPlan, planList));
        setShowPaymentResult(true);
      } else if (paymentStatus === 'cancel') {
        setPaymentResult(buildPaymentCancelledResult());
        setShowPaymentResult(true);
      }

      if (paymentStatus) {
        window.history.replaceState(null, '', window.location.pathname);
      }
    };

    if (billingReturn || paymentStatus) {
      handlePaymentReturn();
    }
  }, [location.pathname, location.search]);

  const hasActiveSubscription = activePlan?.hasBillingSubscription
    || hasManageableSubscriptionStatus(activePlan?.subscriptionStatus);
  const billingIsFullPlan = isFullPlanName(activePlan?.billing);
  const includesAllFeatures = billingIsFullPlan
    || (
      Boolean(activePlan?.permissions?.has_all_in_one)
      && !activePlan?.trial?.isActive
      && hasActiveSubscription
    );
  const showAddonCatalog = sortedAddonPlans.length > 0;
  const billingIsBasic = isBasicPlanName(activePlan?.billing);
  const currentBillingPlanId = activePlan?.billing?._id || activePlan?.billing;
  const activeAddonIdSet = useMemo(
    () => new Set((activePlan?.addons || []).map(String)),
    [activePlan?.addons],
  );

  const activeSubscriptionIncludes = useMemo(() => {
    if (billingIsFullPlan || includesAllFeatures) {
      return ['Trips', 'Zones', 'Reports'];
    }
    if (activePlan?.addonPlans?.length > 0) {
      return activePlan.addonPlans.map((plan) => getDisplayPlanName(plan.planName));
    }
    return sortedAddonPlans
      .filter((plan) => activeAddonIdSet.has(String(plan._id)))
      .map((plan) => getDisplayPlanName(plan.planName));
  }, [
    activeAddonIdSet,
    activePlan?.addonPlans,
    billingIsFullPlan,
    includesAllFeatures,
    sortedAddonPlans,
  ]);

  const formatSubscriptionDate = (dateValue) => {
    if (!dateValue) {
      return '';
    }
    return new Date(dateValue).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const currentPlanName = activePlan?.effective?.planName;
  const cancelScheduled = Boolean(activePlan?.subscription?.cancelAtPeriodEnd);
  const currentPeriodEndLabel = formatSubscriptionDate(
    activePlan?.subscription?.currentPeriodEnd,
  );
  const activeBillingPlanName = getDisplayPlanName(
    activePlan?.billing?.planName || currentPlanName,
  );

  const openBillingPortal = async () => {
    try {
      await openStripeCustomerPortal(postData, returnPath, activePlan);
    } catch (err) {
      showSimpleToast(getPaymentErrorMessage(err));
    }
  };

  const handleCancelSubscription = async () => {
    setIsCanceling(true);
    try {
      const response = await postData(USER_MANAGEMENT.SUBSCRIPTION_CANCEL, {
        atPeriodEnd: true,
      });
      if (response?.status === 200 || response?.status === true) {
        setShowCancelModal(false);
        showSimpleToast(response?.message || 'Subscription cancellation scheduled.');
        await fetchUserDetails(false);
        return;
      }
      showSimpleToast(response?.message || 'Unable to cancel subscription.');
    } catch (error) {
      showSimpleToast(getPaymentErrorMessage(error));
    } finally {
      setIsCanceling(false);
    }
  };

  const handleKeepSubscription = async () => {
    setIsSubmitting(true);
    try {
      const response = await postData(USER_MANAGEMENT.SUBSCRIPTION_REACTIVATE, {});
      if (response?.status === 200 || response?.status === true) {
        showSimpleToast(response?.message || 'Subscription will continue renewing.');
        await fetchUserDetails(false);
        return;
      }
      showSimpleToast(response?.message || 'Unable to keep subscription active.');
    } catch (error) {
      showSimpleToast(getPaymentErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPlanAmount = (plan) => (
    billingPeriod === 'yearly'
      ? (plan?.yearlyPrice ?? plan?.price)
      : (plan?.monthlyPrice ?? plan?.price)
  );

  const hasConfiguredPrice = (plan) => (
    billingPeriod === 'yearly'
      ? Boolean(plan?.stripeYearlyPriceId)
      : Boolean(plan?.stripeMonthlyPriceId || plan?.stripePriceId)
  );

  const getPlanImage = (planName) => imageMap[planName] || Basic_dashboard;

  const updateAddons = async (addonPlanIds) => {
    const response = await postData(USER_MANAGEMENT.SUBSCRIPTION_ADDONS, {
      addonPlanIds,
      atPeriodEnd: false,
    });
    if (response?.status === 200 || response?.status === true) {
      showSimpleToast(response?.message || 'Add-ons updated.');
      fetchUserDetails();
      return;
    }
    showSimpleToast(response?.message || 'Unable to update add-ons.');
  };

  const updateSubscriptionPlan = async (planId) => {
    const response = await postData(USER_MANAGEMENT.SUBSCRIPTION_UPDATE, {
      planId,
      billingPeriod,
    });
    if (response?.status === 200 || response?.status === true) {
      showSimpleToast(response?.message || 'Subscription updated.');
      fetchUserDetails();
      return;
    }
    showSimpleToast(response?.message || 'Unable to update subscription.');
  };

  const startCheckout = async ({ planId, addonPlanIds = [] }) => {
    await redirectToStripeCheckout(postData, {
      planId,
      billingPeriod,
      addonPlanIds,
    }, stripe);
  };

  const handleSubscribe = async (plan) => {
    if (!hasConfiguredPrice(plan)) {
      showSimpleToast('This billing period is not available for the selected plan.');
      return;
    }
    if (includesAllFeatures && isAddonPlanName(plan)) {
      showSimpleToast('Trips, Zones, and Reports are already included in your All-in-One plan.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (hasActiveSubscription) {
        if (isAddonPlanName(plan)) {
          if (!billingIsBasic) {
            showSimpleToast('Add-ons are only available with the Basic plan.');
            return;
          }
          const addonId = String(plan._id);
          const nextAddonIds = activeAddonIdSet.has(addonId)
            ? [...activeAddonIdSet].filter((id) => id !== addonId)
            : [...activeAddonIdSet, addonId];
          await updateAddons(nextAddonIds);
          return;
        }

        if (isBasicPlanName(plan)) {
          await updateAddons(selectedAddonIds);
          return;
        }

        await updateSubscriptionPlan(plan._id);
        return;
      }

      if (isAddonPlanName(plan)) {
        if (!basicPlan) {
          showSimpleToast('Basic plan is not configured.');
          return;
        }
        await startCheckout({
          planId: basicPlan._id,
          addonPlanIds: [plan._id],
        });
        return;
      }

      if (isBasicPlanName(plan)) {
        await startCheckout({
          planId: plan._id,
          addonPlanIds: selectedAddonIds,
        });
        return;
      }

      await startCheckout({
        planId: plan._id,
        addonPlanIds: [],
      });
    } catch (error) {
      showSimpleToast(getPaymentErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSubscriptionChangeLabel = (plan) => {
    if (isAddonPlanName(plan)) {
      return null;
    }
    if (String(plan._id) === String(currentBillingPlanId)) {
      return 'Subscribed';
    }
    if (billingIsFullPlan && isBasicPlanName(plan)) {
      return 'Downgrade';
    }
    if (billingIsBasic && isFullPlanName(plan)) {
      return 'Upgrade';
    }
    return 'Change plan';
  };

  const getButtonLabel = (plan) => {
    if (hasActiveSubscription) {
      if (includesAllFeatures && isAddonPlanName(plan)) {
        return 'Included';
      }
      if (isAddonPlanName(plan)) {
        return activeAddonIdSet.has(String(plan._id)) ? 'Remove add-on' : 'Add add-on';
      }
      return getSubscriptionChangeLabel(plan) || 'Change plan';
    }
    if (String(plan._id) === String(currentBillingPlanId) && !isAddonPlanName(plan)) {
      return 'Subscribed';
    }
    if (isAddonPlanName(plan) && activeAddonIdSet.has(String(plan._id))) {
      return 'Subscribed';
    }
    return 'Subscription Now';
  };

  const isPlanDisabled = (plan) => {
    if (isSubmitting) {
      return true;
    }
    if (!hasConfiguredPrice(plan)) {
      return true;
    }
    if (includesAllFeatures && isAddonPlanName(plan)) {
      return true;
    }
    if (!isAddonPlanName(plan) && String(plan._id) === String(currentBillingPlanId)) {
      return true;
    }
    return false;
  };

  const renderPlanCard = (plan) => {
    const isCurrentBillingPlan = String(plan._id) === String(currentBillingPlanId);
    const isActiveAddon = isAddonPlanName(plan) && activeAddonIdSet.has(String(plan._id));
    const isSubscribed = isCurrentBillingPlan || isActiveAddon;
    const buttonLabel = getButtonLabel(plan);
    const disabled = isPlanDisabled(plan);
    const isBundleIncludedAddon = includesAllFeatures && isAddonPlanName(plan);

    return (
      <div key={plan._id} className="subscription-plan-item">
        <div className={`subscription-blue-card ${isSubscribed ? 'current-plan' : ''} ${isBundleIncludedAddon ? 'included-plan' : ''}`}>
          {isSubscribed && <div className="current-plan-badge">Current</div>}
          <div className="subscription-icons">
            <img src={getPlanImage(plan.planName)} alt="" />
          </div>
          <div className="subscription-title">{getDisplayPlanName(plan.planName)}</div>
          <div className="subscription-list">
            <ul>
              {plan.description?.map((feature) => (
                <li key={`${plan._id}-${feature}`}>{feature}</li>
              ))}
            </ul>
          </div>
          <div className="price-footer">
            <div className="price-text">
              {formatCurrencyAmount(getPlanAmount(plan))}
              <span> /{billingPeriod === 'yearly' ? 'yr' : 'mo'}</span>
              {billingPeriod === 'yearly' && (
                <small className="subscription-billed-note"> Billed annually</small>
              )}
            </div>
            <button
              type="button"
              onClick={() => handleSubscribe(plan)}
              className={`btn ${isSubscribed ? 'subscribed-button' : 'subscription-button'}`}
              disabled={disabled}
            >
              {buttonLabel}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={`subscription-toolbar ${embedded ? 'subscription-toolbar-embedded' : ''}`}>
        <div>
          {!embedded && <h4>Choose your plan</h4>}
          {!embedded && (
            <p>Select a subscription or add-on below. Checkout opens securely in Stripe.</p>
          )}
          {embedded && <h4 className="subscription-embedded-title">Subscription plans</h4>}
          {currentPlanName && (
            <div className="current-access-summary">
              Current access: <strong>{currentPlanName}</strong>
              {activePlan?.trial?.isActive && <span>Free trial</span>}
            </div>
          )}
        </div>
        <div className="billing-toggle" aria-label="Billing period">
          <label className={billingPeriod === 'monthly' ? 'active' : ''}>
            <input
              type="radio"
              name="billingPeriod"
              value="monthly"
              checked={billingPeriod === 'monthly'}
              onChange={() => setBillingPeriod('monthly')}
            />
            Monthly
          </label>
          <label className={billingPeriod === 'yearly' ? 'active' : ''}>
            <input
              type="radio"
              name="billingPeriod"
              value="yearly"
              checked={billingPeriod === 'yearly'}
              onChange={() => setBillingPeriod('yearly')}
            />
            Yearly
          </label>
        </div>
      </div>

      {hasActiveSubscription && (
        <div className={`active-subscription-panel ${cancelScheduled ? 'active-subscription-panel--canceling' : ''}`}>
          <div className="active-subscription-heading">
            <div>
              <span className="active-subscription-label">
                {cancelScheduled ? 'Cancellation scheduled' : 'Active subscription'}
              </span>
              <h5>{activeBillingPlanName}</h5>
              {currentPeriodEndLabel && (
                <p className="active-subscription-renewal">
                  {cancelScheduled
                    ? `Access ends on ${currentPeriodEndLabel}`
                    : `Renews on ${currentPeriodEndLabel}`}
                </p>
              )}
              {activeSubscriptionIncludes.length > 0 && (
                <p className="active-subscription-includes">
                  {billingIsFullPlan || includesAllFeatures
                    ? 'Includes all features: '
                    : 'Active add-ons: '}
                  {activeSubscriptionIncludes.join(', ')}
                </p>
              )}
            </div>
            <div className="active-subscription-actions">
              <button
                type="button"
                className="btn subscription-button"
                onClick={openBillingPortal}
                disabled={isSubmitting || isCanceling}
              >
                Manage payment method
              </button>
              {cancelScheduled ? (
                <button
                  type="button"
                  className="btn subscription-button subscription-button-secondary"
                  onClick={handleKeepSubscription}
                  disabled={isSubmitting || isCanceling}
                >
                  Keep subscription
                </button>
              ) : (
                <button
                  type="button"
                  className="btn subscription-button subscription-button-outline"
                  onClick={() => setShowCancelModal(true)}
                  disabled={isSubmitting || isCanceling}
                >
                  Cancel subscription
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="subscription-base-plans">
        {subscriptionPlans.map((plan) => renderPlanCard(plan))}
      </div>

      {showAddonCatalog && (
        <div className="subscription-addon-plans">
          {sortedAddonPlans.map((plan) => renderPlanCard(plan))}
        </div>
      )}

      <CancelSubscriptionModal
        show={showCancelModal}
        planName={activeBillingPlanName}
        periodEndLabel={currentPeriodEndLabel}
        isSubmitting={isCanceling}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelSubscription}
      />

      {showPaymentResult && (
        <PaymentResultModal
          result={paymentResult}
          onClose={() => {
            setShowPaymentResult(false);
            setPaymentResult(null);
          }}
        />
      )}
      {showToast && toastMessage && (
        <PaymentResultModal
          result={{ variant: 'success', title: toastMessage, subtitle: '', rows: [] }}
          onClose={() => {
            setShowToast(false);
            setToastMessage('');
          }}
        />
      )}
    </>
  );
}

export default Subscription;
